// RechercheIntel.jsx

import React, { useEffect, useState, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { VariableSizeList } from 'react-window';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Grid,
  useTheme,
  useMediaQuery,
  SwipeableDrawer,
  Fab,
  Divider,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Tooltip,
  Chip,
  Autocomplete,
  Pagination,        // <-- Pour la pagination mobile
  TablePagination,   // <-- Pour la pagination en vue desktop
  alpha,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { Link } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import PublicIcon from '@mui/icons-material/Public';
import EventIcon from '@mui/icons-material/Event';
import GroupIcon from '@mui/icons-material/Group';

import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { canModifyRecord } from '../utils/permissions';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import RechercheIntelContext from './RechercheIntelContext';
import { usePositions } from '../utils/positions';
import PlayerTypeToggle from '../components/PlayerTypeToggle';
import { matchesPlayerTypeValue } from '../utils/playerTypes';
import { currentUserCanUseProspectFilter } from '../utils/prospectUtils';

// -- Configuration de la virtualisation pour Autocomplete
const LISTBOX_PADDING = 8;
function renderRow(props) {
  const { data, index, style } = props;
  const item = data[index];
  return React.cloneElement(item, {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  return (
    <div
      ref={ref}
      {...props}
      {...outerProps}
      style={{
        ...props.style,
        overflowX: 'hidden',
        overflowY: 'scroll',
        maxWidth: '100%',
      }}
    />
  );
});

function useResetCache(data) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const ListboxComponent = React.forwardRef((props, ref) => {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 48;

  const getChildSize = () => itemSize;

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemCount * itemSize;
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={getChildSize}
          overscanCount={5}
          itemCount={itemCount}
          style={{
            padding: 0,
            margin: 0,
            overflowX: 'hidden',
          }}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});


// -- Fonctions utilitaires 
const getInitials = (nom, prenom) => {
  const firstInitial = nom ? nom[0] : '';
  const secondInitial = prenom ? prenom[0] : '';
  return (firstInitial + secondInitial).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return '#757575';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 70%, 50%)`;
};

const getAvatarUrl = (userId) => {
  return userId
    ? `${getMediaBaseUrl()}/users/${userId}.png?${new Date().getTime()}`
    : '/path/to/default-avatar.png';
};

const getPlayerAvatarUrl = (playerId) => {
  return playerId
    ? `${getMediaBaseUrl()}/photos/${playerId}.png?${new Date().getTime()}`
    : '/path/to/default-player-avatar.png';
};

const getShortNationality = (nationality, t) => {
  if (!nationality) return '';
  // 1. Vérifier si la valeur contient déjà des parenthèses (ancien format)
  const directMatch = nationality.match(/\((.*?)\)/);
  if (directMatch) return directMatch[1];
  // 2. Sinon, essayer de traduire la clé (nouveau format)
  const translatedNationality = t ? t(`createPlayer.nationalities.${nationality}`, { defaultValue: '' }) : '';
  if (translatedNationality) {
    const translatedMatch = translatedNationality.match(/\((.*?)\)/);
    if (translatedMatch) return translatedMatch[1];
  }
  // 3. Retourner la valeur brute si rien ne fonctionne
  return nationality;
};

// Ignore les valeurs "null"/"undefined" stockées en texte dans la BDD et joint les ligues restantes
const formatLeagues = (...leagues) =>
  leagues.filter((l) => l && l !== 'null' && l !== 'undefined').join(' - ');

const getDecimalAge = (fecha, dob) => {
  if (!fecha || !dob) return '';
  const f = new Date(fecha);
  const d = new Date(dob);

  const diffMs = f - d;
  if (diffMs < 0) return '';

  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  const diffYears = diffDays / 365;

  return diffYears.toFixed(1);
};

// -- Styled Components pour le contenu interne de l'accordéon (alignés sur IntelTab)
const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.mode === 'light'
    ? theme.palette.grey[50]
    : alpha(theme.palette.background.paper, 0.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  '& p': {
    marginBottom: theme.spacing(1.5),
    lineHeight: 1.7,
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  marginRight: theme.spacing(0.5),
  verticalAlign: 'middle',
  color: theme.palette.text.secondary,
}));

// -- Composant carte (Mobile)
const MobileIntelCard = ({ intel, onEdit, onDelete, open, onToggle, user, t, translateFromDB }) => {
  const theme = useTheme();
  const decimalAge = getDecimalAge(intel.fecha, intel.player_date_naissance);

  return (
    <Card sx={{ mb: 2, boxShadow: theme.shadows[2] }}>
      <CardHeader
        avatar={
          <Avatar src={getPlayerAvatarUrl(intel.player_id)} alt={intel.player_nom} />
        }
        title={
          user?.role?.toLowerCase() === 'entrenador' || user?.role?.toLowerCase() === 'pdp' ? (
            <Typography>{intel.player_nom}</Typography>
          ) : (
            <Link
              to={`/players/${intel.player_id}`}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              {intel.player_nom}
            </Link>
          )
        }
        subheader={<Typography variant="body2">{new Date(intel.fecha).toLocaleDateString()}</Typography>}
        action={
          <IconButton onClick={() => onToggle(intel.id)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        }
      />
      <Collapse in={open} timeout="auto" unmountOnExit>
        <ContentBox>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                src={getAvatarUrl(intel.usuario_id)}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: getAvatarColor(intel.usuario_nom + intel.usuario_prenom),
                  fontSize: '0.8rem',
                }}
              >
                {getInitials(intel.usuario_prenom, intel.usuario_nom)}
              </Avatar>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {intel.usuario_prenom} {intel.usuario_nom}
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                  <IconWrapper><SportsBasketballIcon sx={{ fontSize: 14 }} /></IconWrapper>
                  {t('rechercheIntel.position')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.3 }}>
                  {translateFromDB(intel.player_poste)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                  <IconWrapper><EventIcon sx={{ fontSize: 14 }} /></IconWrapper>
                  {t('rechercheIntel.year')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.3 }}>
                  {intel.player_annee_naissance} {decimalAge && `(${decimalAge})`}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                  <IconWrapper><PublicIcon sx={{ fontSize: 14 }} /></IconWrapper>
                  {t('rechercheIntel.nationality')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.3 }}>
                  {getShortNationality(intel.player_nationalite, t)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                  {t('rechercheIntel.source')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.3 }}>
                  {intel.fuente}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                  <IconWrapper><GroupIcon sx={{ fontSize: 14 }} /></IconWrapper>
                  {t('rechercheIntel.team')}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {!!intel.player_is_free_agent && (
                    <Chip
                      label={t('createPlayer.fields.freeAgentBadge')}
                      size="small"
                      sx={{
                        backgroundColor: '#4caf50',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '11px',
                        height: '22px',
                        mb: 0.5,
                      }}
                    />
                  )}
                  {intel.player_competitions_detail && intel.player_competitions_detail.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {[...new Set(intel.player_competitions_detail.map(c => c.equipe_nom))].map((equipe, idx) => {
                        const equipeData = intel.player_competitions_detail.find(c => c.equipe_nom === equipe);
                        const competitions = intel.player_competitions_detail
                          .filter(c => c.equipe_nom === equipe)
                          .filter((value, index, self) =>
                            index === self.findIndex((c) => c.competition_id === value.competition_id)
                          );
                        const mediaUrl = getMediaBaseUrl();
                        const timestamp = Date.now();

                        return (
                          <Box key={idx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Box
                                component="img"
                                src={`${mediaUrl}/equipes/${equipeData.equipe_id}.jpg?t=${timestamp}`}
                                alt={equipe}
                                sx={{
                                  width: 20,
                                  height: 20,
                                  objectFit: 'contain',
                                  borderRadius: '2px',
                                }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{equipe}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', ml: 3.5, mt: 0.3 }}>
                              {competitions.map((comp, compIdx) => (
                                <Box key={compIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                  <Box
                                    component="img"
                                    src={`${mediaUrl}/competitions/${comp.competition_id}.jpg?t=${timestamp}`}
                                    alt={comp.competition_nom}
                                    sx={{
                                      width: 14,
                                      height: 14,
                                      objectFit: 'contain',
                                      borderRadius: '2px',
                                    }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                  <Typography variant="caption" color="text.secondary">{comp.competition_nom}</Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body2">
                        {intel.player_team && intel.player_team !== 'null' ? intel.player_team : 'N/A'}
                      </Typography>
                      {formatLeagues(intel.player_league_1, intel.player_league_2) && (
                        <Typography variant="caption" color="text.secondary">
                          {formatLeagues(intel.player_league_1, intel.player_league_2)}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Divider />

            <Box>
              <Typography
                variant="body2"
                component="div"
                dangerouslySetInnerHTML={{ __html: intel.detalles }}
                sx={{
                  '& p': { marginBottom: 1 },
                  '& br': { display: 'block', content: '""', marginTop: 0.5 },
                }}
              />
            </Box>
          </Stack>
        </ContentBox>
        {canModifyRecord(user, intel.usuario_id) && (
          <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
            <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(intel)}>
              {t('rechercheIntel.edit')}
            </Button>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => onDelete(intel)}
            >
              {t('rechercheIntel.delete')}
            </Button>
          </CardActions>
        )}
      </Collapse>
    </Card>
  );
};


// -- Tiroir de filtres (Mobile)
const MobileFilterDrawer = ({
  open,
  onClose,
  players,
  users,
  selectedPlayer,
  selectedUser,
  onPlayerChange,
  onUserChange,
  selectedPlayerType,
  onPlayerTypeChange,
  isSuperAdmin,
  t,
}) => {
  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      sx={{
        '& .MuiDrawer-paper': {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '80vh',
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">{t('rechercheIntel.filters')}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Autocomplete
                options={players}
                getOptionLabel={(player) => player.nom}
                value={selectedPlayer}
                onChange={onPlayerChange}
                ListboxComponent={ListboxComponent}
                renderInput={(params) => (
                  <TextField {...params} label={t('rechercheIntel.selectPlayer')} variant="outlined" />
                )}
                renderOption={(props, player) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                      src={getPlayerAvatarUrl(player.id)}
                      alt={player.nom}
                      sx={{ mr: 1, width: 32, height: 32 }}
                    />
                    {player.nom}
                  </Box>
                )}
                clearOnEscape
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Autocomplete
                options={users}
                getOptionLabel={(user) => `${user.prenom} ${user.nom}`}
                value={selectedUser}
                onChange={onUserChange}
                ListboxComponent={ListboxComponent}
                renderInput={(params) => (
                  <TextField {...params} label={t('rechercheIntel.selectUser')} variant="outlined" />
                )}
                renderOption={(props, user) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                      src={getAvatarUrl(user.id)}
                      alt={user.nom}
                      sx={{ mr: 1, width: 32, height: 32 }}
                    />
                    {user.prenom} {user.nom}
                  </Box>
                )}
                clearOnEscape
              />
            </FormControl>
          </Grid>
          {isSuperAdmin && (
            <Grid item xs={12}>
              <PlayerTypeToggle
                value={selectedPlayerType}
                onChange={onPlayerTypeChange}
                t={t}
                tPrefix="rechercheIntel"
              />
            </Grid>
          )}
        </Grid>
      </Box>
    </SwipeableDrawer>
  );
};

// -- Composant principal
const RechercheIntel = ({ onDataLoaded }) => {
  const { t } = useTranslation();
  const { translateFromDB } = usePositions(t);

  // === On récupère tout depuis le contexte ===
  const {
    selectedPlayer,
    setSelectedPlayer,
    selectedUser,
    setSelectedUser,
    selectedPlayerType,
    setSelectedPlayerType,
    openRows,
    toggleRow,
  } = useContext(RechercheIntelContext);

  // État local pour les INTEL chargés
  const [allIntelData, setAllIntelData] = useState([]);
  const [intelData, setIntelData] = useState([]);

  // Édition / Suppression
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [currentIntel, setCurrentIntel] = useState(null);

  // On va stocker séparément les champs à éditer :
  const [editedDetalles, setEditedDetalles] = useState('');
  const [editedFuente, setEditedFuente] = useState('');
  const [editedFecha, setEditedFecha] = useState('');

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [intelToDelete, setIntelToDelete] = useState(null);

  // Filtres : listes (players, users) pour l'autocomplete
  const [players, setPlayers] = useState([]);
  const [users, setUsers] = useState([]);

  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);

  const [user, setUser] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);

  // --- ÉTATS POUR LA PAGINATION ---
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Récupération + filtrage initial
  useEffect(() => {
    const fetchAllIntel = async () => {
      try {
        setLoading(true);

        // 1) Récupération user localStorage
        const localStoredUser = JSON.parse(localStorage.getItem('user'));

        // 2) Récupération en parallèle :
        // - la liste des intel
        // - la liste de tous les users (pour enrichir l'utilisateur loggué)
        const [resIntel, resUsers] = await Promise.all([
          api.get('/api/intel/all'),
          api.get('/api/users'),
        ]);

        let dataIntel = resIntel.data || [];

        // 3) Récupération de la liste complète des users
        const allUsers = Array.isArray(resUsers.data)
          ? resUsers.data
          : resUsers.data.users || [];

        // 4) On enrichit l'utilisateur loggué avec ses groupes, si possible
        let finalUser = localStoredUser;
        if (localStoredUser) {
          const foundFullUser = allUsers.find(u => u.id === localStoredUser.id);
          if (foundFullUser) {
            finalUser = foundFullUser;
          }
        }
        setUser(finalUser);
        setUsers(allUsers);

        // 5) Filtrage selon le rôle + groupes
        if (finalUser) {
          const roleLower = finalUser.role?.toLowerCase();
          if (roleLower === 'entrenador' || roleLower === 'pdp') {
            // Intersection de groupes
            const userGroups = finalUser.groupes || [];
            dataIntel = dataIntel.filter((item) => {
              // item.usuario_id => créateur du Intel
              const createdByUser = (item.usuario_id === finalUser.id);
              // item.player_groupes => groupes du joueur associé à ce Intel
              const playerGroups = item.player_groupes || [];
              const hasIntersection = playerGroups.some((pg) =>
                userGroups.some((ug) => ug.id === pg.id)
              );
              return createdByUser || hasIntersection;
            });
          } else if (roleLower === 'admin pro') {
            dataIntel = dataIntel.filter(
              (item) =>
                item.player_type?.toLowerCase() === 'pro' ||
                item.usuario_id === finalUser.id
            );
          } else if (roleLower === 'admin cantera') {
            dataIntel = dataIntel.filter(
              (item) =>
                item.player_type?.toLowerCase() === 'cantera' ||
                item.usuario_id === finalUser.id
            );
          } else if (roleLower === 'scout interne' || roleLower === 'scout externe') {
            // Les scouts ne voient que leurs propres intels
            dataIntel = dataIntel.filter((item) => item.usuario_id === finalUser.id);
          } else if (roleLower === 'scout interne cantera' || roleLower === 'scout externe cantera') {
            // Les scouts cantera ne voient que leurs propres intels sur joueurs cantera
            dataIntel = dataIntel.filter((item) => item.player_type?.toLowerCase() === 'cantera' && item.usuario_id === finalUser.id);
          }
        }

        setAllIntelData(dataIntel);
        setIntelData(dataIntel);

        if (onDataLoaded) {
          onDataLoaded(dataIntel.length > 0);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des Intel:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllIntel();
  }, [backendUrl, section, onDataLoaded]);

  // Récupération des joueurs (pour l'autocomplete)
  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const response = await api.get('/api/players/select');
        setPlayers(
          Array.isArray(response.data) ? response.data : response.data.players || []
        );
      } catch (error) {
        console.error('Erreur lors de la récupération des joueurs:', error);
        setPlayers([]);
      }
    };
    fetchPlayers();
  }, [backendUrl, section]);

  // Filtrage local (joueur, user, année, position)
  useEffect(() => {
    let filteredData = allIntelData;

    if (selectedPlayer) {
      filteredData = filteredData.filter(
        (intel) => intel.player_id === selectedPlayer.id
      );
    }
    if (selectedUser) {
      filteredData = filteredData.filter(
        (intel) => intel.usuario_id === selectedUser.id
      );
    }
    if (selectedPlayerType && selectedPlayerType !== 'all') {
      // Sous PRO, on écarte le vivier « NCAA + » — même gate que l'affichage du bucket.
      // Hissé HORS du callback : sinon localStorage serait relu à chaque ligne.
      const excludeProspects = currentUserCanUseProspectFilter();
      filteredData = filteredData.filter((intel) => matchesPlayerTypeValue(intel, selectedPlayerType, { excludeProspects }));
    }

    setIntelData(filteredData);
    setPage(0);
  }, [
    allIntelData,
    selectedPlayer,
    selectedUser,
    selectedPlayerType,
  ]);

  // Récupération des utilisateurs (déjà fait plus haut, dans fetchAllIntel + resUsers)
  // On setUsers() dedans.

  // === Pagination : découpe des données ===
  const totalResults = intelData.length;

  const paginatedData = React.useMemo(() => {
    if (rowsPerPage === -1) {
      // Cas "Tous"
      return intelData;
    }
    return intelData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [intelData, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newVal = parseInt(event.target.value, 10);
    setRowsPerPage(newVal);
    setPage(0);
  };

  const handleChangeMobilePage = (event, newValue) => {
    setPage(newValue - 1);
  };

  const informesFoundLabel = totalResults === 1 ? t('rechercheIntel.intelFound') : t('rechercheIntel.intelsFound');

  // -- Gestionnaires d'édition
  const handleEditClick = (intel) => {
    setCurrentIntel(intel);
    setEditedDetalles(intel.detalles || '');
    setEditedFuente(intel.fuente || '');

    const dateStr = intel.fecha ? new Date(intel.fecha).toISOString().split('T')[0] : '';
    setEditedFecha(dateStr);

    setOpenEditDialog(true);
  };

  const handleEditSave = async () => {
    try {
      const transformedDetalles = editedDetalles
        .replace(/<p><br><\/p>/g, '<br>')
        .replace(/<p>/g, '')
        .replace(/<\/p>/g, '<br>')
        .replace(/<br><br>/g, '<br>');

      const payload = {
        detalles: transformedDetalles,
        fuente: editedFuente,
        fecha: editedFecha,
      };

      await api.put(`/api/intel/${currentIntel.id}`, payload);

      setAllIntelData((prevAll) =>
        prevAll.map((item) =>
          item.id === currentIntel.id
            ? {
                ...item,
                detalles: transformedDetalles,
                fuente: editedFuente,
                fecha: editedFecha,
              }
            : item
        )
      );

      setOpenEditDialog(false);
      alert(t('rechercheIntel.intelUpdatedSuccess'));
    } catch (error) {
      console.error('Error al actualizar el Intel:', error);
      alert(t('rechercheIntel.intelUpdateError'));
    }
  };

  // -- Gestionnaires de suppression
  const handleDeleteClick = (intel) => {
    setIntelToDelete(intel);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/api/intel/${intelToDelete.id}`);
      setAllIntelData((prevAll) => prevAll.filter((item) => item.id !== intelToDelete.id));
      setOpenDeleteDialog(false);
      alert(t('rechercheIntel.intelDeletedSuccess'));
    } catch (error) {
      console.error('Error al eliminar el Intel:', error);
      alert(t('rechercheIntel.intelDeleteError'));
    }
  };

  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setIntelToDelete(null);
  };

  // Toggle collapse (mobile + desktop)
  const handleToggleRow = (id) => {
    toggleRow(id);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <Typography variant="h6">{t('rechercheIntel.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', pb: isMobile ? 7 : 0 }}>
      {isMobile ? (
        <>
          {/* ====================== VUE MOBILE ====================== */}
          <Box
            sx={{
              mx: 2,
              mt: 2,
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              p: 2,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              borderRadius: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                fontWeight: 'bold',
                fontSize: '1.1rem',
              }}
            >
              {totalResults}
            </Box>
            <Typography variant="subtitle1">
              {informesFoundLabel}
            </Typography>
          </Box>

          <Box sx={{ p: 2 }}>
            {paginatedData.map((intel) => (
              <MobileIntelCard
                key={intel.id}
                intel={intel}
                user={user}
                open={openRows[intel.id] || false}
                onToggle={handleToggleRow}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                t={t}
                translateFromDB={translateFromDB}
              />
            ))}
          </Box>

          {/* Pagination mobile (si rowsPerPage != -1) */}
          {rowsPerPage !== -1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <Pagination
                color="primary"
                count={Math.ceil(totalResults / rowsPerPage)}
                page={page + 1}
                onChange={handleChangeMobilePage}
              />
            </Box>
          )}

          {/* Bouton de filtre mobile */}
          <Fab
            color="primary"
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              zIndex: theme.zIndex.fab,
            }}
            onClick={() => setOpenFilterDrawer(true)}
          >
            <FilterListIcon />
          </Fab>

          {/* Tiroir de filtres mobile */}
          <MobileFilterDrawer
            open={openFilterDrawer}
            onClose={() => setOpenFilterDrawer(false)}
            players={players}
            users={users}
            selectedPlayer={selectedPlayer}
            selectedUser={selectedUser}
            onPlayerChange={(_, newValue) => setSelectedPlayer(newValue)}
            onUserChange={(_, newValue) => setSelectedUser(newValue)}
            selectedPlayerType={selectedPlayerType}
            onPlayerTypeChange={(val) => setSelectedPlayerType(val)}
            isSuperAdmin={user?.role?.toLowerCase() === 'super admin'}
            t={t}
          />
        </>
      ) : (
        <>
          {/* ====================== VUE DESKTOP ====================== */}
          <Grid container spacing={2} marginBottom={2}>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <Autocomplete
                  options={players}
                  getOptionLabel={(player) => player.nom}
                  value={selectedPlayer}
                  onChange={(_, newValue) => setSelectedPlayer(newValue)}
                  ListboxComponent={ListboxComponent}
                  renderInput={(params) => (
                    <TextField {...params} label={t('rechercheIntel.selectPlayer')} variant="outlined" />
                  )}
                  renderOption={(props, player) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        src={getPlayerAvatarUrl(player.id)}
                        alt={player.nom}
                        sx={{ mr: 1 }}
                      />
                      {player.nom}
                    </Box>
                  )}
                  clearOnEscape
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <Autocomplete
                  options={users}
                  getOptionLabel={(u) => `${u.prenom} ${u.nom}`}
                  value={selectedUser}
                  onChange={(_, newValue) => setSelectedUser(newValue)}
                  ListboxComponent={ListboxComponent}
                  renderInput={(params) => (
                    <TextField {...params} label={t('rechercheIntel.selectUser')} variant="outlined" />
                  )}
                  renderOption={(props, u) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        src={getAvatarUrl(u.id)}
                        alt={u.nom}
                        sx={{ width: 30, height: 30, mr: 1 }}
                      />
                      {u.prenom} {u.nom}
                    </Box>
                  )}
                  clearOnEscape
                />
              </FormControl>
            </Grid>
            {user?.role?.toLowerCase() === 'super admin' && (
              <Grid item xs={12} sm={3}>
                <PlayerTypeToggle
                  value={selectedPlayerType}
                  onChange={(val) => setSelectedPlayerType(val)}
                  t={t}
                  tPrefix="rechercheIntel"
                />
              </Grid>
            )}
          </Grid>

          <Box
            sx={{
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              p: 2,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              borderRadius: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                fontWeight: 'bold',
                fontSize: '1.2rem',
              }}
            >
              {totalResults}
            </Box>
            <Typography variant="h6">
              {informesFoundLabel}
            </Typography>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('rechercheIntel.creator')}</TableCell>
                  <TableCell>{t('rechercheIntel.date')}</TableCell>
                  <TableCell>{t('rechercheIntel.player')}</TableCell>
                  <TableCell>{t('rechercheIntel.position')}</TableCell>
                  <TableCell>{t('rechercheIntel.year')}</TableCell>
                  <TableCell>{t('rechercheIntel.nationality')}</TableCell>
                  <TableCell>{t('rechercheIntel.team')}</TableCell>
                  <TableCell>{t('rechercheIntel.source')}</TableCell>
                  <TableCell>{t('rechercheIntel.details')}</TableCell>
                  <TableCell>{t('rechercheIntel.action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row) => {
                  const decimalAge = getDecimalAge(row.fecha, row.player_date_naissance);
                  const shortNationality = getShortNationality(row.player_nationalite, t);

                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        hover
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                        onClick={(e) => {
                          // Ne pas toggler si on clique sur un lien, bouton ou action
                          if (
                            e.target.closest('a') ||
                            e.target.closest('button') ||
                            e.target.closest('[role="button"]')
                          ) {
                            return;
                          }
                          toggleRow(row.id);
                        }}
                      >
                        {/* Creador */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Tooltip
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: 'white',
                                    '& .MuiTooltip-arrow': { color: 'white' },
                                    boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
                                  },
                                },
                              }}
                              title={
                                <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Avatar
                                    src={getAvatarUrl(row.usuario_id)}
                                    sx={{ width: 50, height: 50 }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 'bold', color: 'black' }}
                                    >
                                      {row.usuario_prenom} {row.usuario_nom}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)' }}>
                                      {row.usuario_nombre}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                            >
                              <Avatar
                                sx={{
                                  mr: 1,
                                  bgcolor: getAvatarColor(
                                    row.usuario_nom + row.usuario_prenom
                                  ),
                                }}
                              >
                                {getInitials(row.usuario_prenom, row.usuario_nom)}
                              </Avatar>
                            </Tooltip>
                          </Box>
                        </TableCell>

                        {/* Fecha */}
                        <TableCell>
                          {new Date(row.fecha).toLocaleDateString()}
                        </TableCell>

                        {/* Joueur */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar
                              src={getPlayerAvatarUrl(row.player_id)}
                              alt={row.player_nom}
                              sx={{ mr: 1 }}
                            />
                            {user?.role?.toLowerCase() === 'entrenador' ||
                            user?.role?.toLowerCase() === 'pdp' ? (
                              <Typography variant="body2">{row.player_nom}</Typography>
                            ) : (
                              <Typography variant="body2">
                                <Link
                                  to={`/players/${row.player_id}`}
                                  style={{ textDecoration: 'none', color: 'inherit' }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {row.player_nom}
                                </Link>
                              </Typography>
                            )}
                          </Box>
                        </TableCell>

                        {/* Posición */}
                        <TableCell>{translateFromDB(row.player_poste)}</TableCell>

                        {/* Año */}
                        <TableCell>
                          {row.player_annee_naissance}
                          {decimalAge && ` (${decimalAge})`}
                        </TableCell>

                        {/* Nationalidad */}
                        <TableCell>{shortNationality}</TableCell>

                        {/* Equipo */}
                        <TableCell>
                          <Box>
                            {!!row.player_is_free_agent && (
                              <Chip
                                label={t('createPlayer.fields.freeAgentBadge')}
                                size="small"
                                sx={{
                                  backgroundColor: '#4caf50',
                                  color: 'white',
                                  fontWeight: 'bold',
                                  fontSize: '10px',
                                  height: '20px',
                                  mb: 0.5,
                                }}
                              />
                            )}
                            {/* Si on a des données dans competitions_detail, les afficher en priorité */}
                            {row.player_competitions_detail && row.player_competitions_detail.length > 0 ? (
                              <>
                                {/* Grouper par équipe */}
                                {[...new Set(row.player_competitions_detail.map(c => c.equipe_nom))].map((equipe, idx) => {
                                  // Récupérer l'ID de l'équipe et les compétitions associées
                                  const equipeData = row.player_competitions_detail.find(c => c.equipe_nom === equipe);
                                  const competitions = row.player_competitions_detail
                                    .filter(c => c.equipe_nom === equipe)
                                    .filter((value, index, self) =>
                                      index === self.findIndex((c) => c.competition_id === value.competition_id)
                                    );
                                  const mediaUrl = getMediaBaseUrl();
                                  const timestamp = Date.now();

                                  return (
                                    <Box key={idx} sx={{ mb: 0.5 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {/* Logo de l'équipe */}
                                        <Box
                                          component="img"
                                          src={`${mediaUrl}/equipes/${equipeData.equipe_id}.jpg?t=${timestamp}`}
                                          alt={equipe}
                                          sx={{
                                            width: 16,
                                            height: 16,
                                            objectFit: 'contain',
                                            borderRadius: '2px'
                                          }}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                        <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{equipe}</div>
                                      </Box>
                                      {/* Logos et noms des compétitions */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, flexWrap: 'wrap', mt: 0.3 }}>
                                        {competitions.map((comp, compIdx) => (
                                          <Box key={compIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                                            <Box
                                              component="img"
                                              src={`${mediaUrl}/competitions/${comp.competition_id}.jpg?t=${timestamp}`}
                                              alt={comp.competition_nom}
                                              sx={{
                                                width: 14,
                                                height: 14,
                                                objectFit: 'contain',
                                                borderRadius: '2px'
                                              }}
                                              onError={(e) => {
                                                e.target.style.display = 'none';
                                              }}
                                            />
                                            <span style={{ fontSize: '10px' }}>{comp.competition_nom}</span>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </>
                            ) : (
                              // Sinon afficher l'ancien système (team et optionnellement les ligues)
                              <Box>
                                {row.player_team && row.player_team !== 'null' && (
                                  <div style={{ fontSize: '12px' }}>{row.player_team}</div>
                                )}
                                {formatLeagues(row.player_league_1, row.player_league_2) && (
                                  <div style={{ fontSize: '10px', color: '#666' }}>
                                    {formatLeagues(row.player_league_1, row.player_league_2)}
                                  </div>
                                )}
                              </Box>
                            )}
                          </Box>
                        </TableCell>

                        {/* Fuente */}
                        <TableCell>{row.fuente}</TableCell>

                        {/* Détails (collapse) */}
                        <TableCell>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleRow(row.id);
                            }}
                          >
                            {openRows[row.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>

                        {/* Actions */}
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {canModifyRecord(user, row.usuario_id) && (
                            <>
                              <IconButton onClick={() => handleEditClick(row)} color="primary">
                                <EditIcon />
                              </IconButton>
                              <IconButton onClick={() => handleDeleteClick(row)} color="error">
                                <DeleteIcon />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Ligne collapsée */}
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0, border: 0 }} colSpan={100}>
                          <Collapse in={openRows[row.id]} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                m: 2,
                                p: 2.5,
                                backgroundColor: alpha(theme.palette.grey[500], 0.06),
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2,
                              }}
                            >
                              <Typography
                                variant="body2"
                                component="div"
                                dangerouslySetInnerHTML={{ __html: row.detalles }}
                                sx={{
                                  '& p': { marginBottom: 1 },
                                  '& br': { display: 'block', content: '""', marginTop: 0.5 },
                                }}
                              />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>

            {/* === Pagination desktop === */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: t('rechercheIntel.all'), value: -1 }]}
              component="div"
              count={totalResults}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </>
      )}

      {/* Dialog d'édition */}
      <Dialog
        open={openEditDialog}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return; // Empêche la fermeture sur clic extérieur
          setOpenEditDialog(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t('rechercheIntel.editIntel')}</DialogTitle>
        <DialogContent>
          {currentIntel && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                <strong>{t('rechercheIntel.date')}:</strong>
              </Typography>
              <TextField
                type="date"
                fullWidth
                value={editedFecha}
                onChange={(e) => setEditedFecha(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Typography variant="subtitle1" gutterBottom>
                <strong>{t('rechercheIntel.source')}:</strong>
              </Typography>
              <TextField
                fullWidth
                value={editedFuente}
                onChange={(e) => setEditedFuente(e.target.value)}
                placeholder={t('rechercheIntel.editSourcePlaceholder')}
                sx={{ mb: 2 }}
              />

              <Typography variant="subtitle1" gutterBottom>
                <strong>{t('rechercheIntel.details')}:</strong>
              </Typography>
              <ReactQuill
                theme="snow"
                value={editedDetalles}
                onChange={setEditedDetalles}
                placeholder={t('rechercheIntel.editDetailsPlaceholder')}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)} color="secondary">
            {t('rechercheIntel.cancel')}
          </Button>
          <Button onClick={handleEditSave} color="primary" variant="contained">
            {t('rechercheIntel.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de suppression */}
      <Dialog
        open={openDeleteDialog}
        onClose={cancelDelete}
        aria-labelledby="delete-confirmation-dialog"
      >
        <DialogTitle id="delete-confirmation-dialog">{t('rechercheIntel.deleteIntel')}</DialogTitle>
        <DialogContent>
          <Typography>{t('rechercheIntel.confirmDeleteMessage')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="secondary">
            {t('rechercheIntel.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="primary" variant="contained">
            {t('rechercheIntel.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RechercheIntel;
